import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import { Plan_Type_Enum } from 'types/databaseEnums';
import { TanstackQueryName } from 'types/frontend';

/**
 * Pure logic function — determines whether polling should continue.
 * Extracted for easy unit testing without React dependencies.
 */
export const shouldKeepPolling = (
  currentPlan: string | undefined,
  expectedNonFreePlan: boolean,
  elapsedMs: number,
  timeoutMs: number
): boolean => {
  if (!expectedNonFreePlan) return false;
  if (elapsedMs >= timeoutMs) return false;
  if (!currentPlan) return true;
  return currentPlan === Plan_Type_Enum.Free;
};

interface UseWaitForPlanUpdateParams {
  enabled: boolean;
  timeoutMs?: number;
  intervalMs?: number;
}

interface UseWaitForPlanUpdateResult {
  isWaiting: boolean;
  didTimeout: boolean;
  hasPollingResolved: boolean;
}

/**
 * Polls the GetCurrentUser query until user.plan changes from Free,
 * or a timeout is reached. Used after Stripe checkout to wait for
 * the webhook to update the user's plan in the database.
 */
export const useWaitForPlanUpdate = ({
  enabled,
  timeoutMs = 30000,
  intervalMs = 2000,
}: UseWaitForPlanUpdateParams): UseWaitForPlanUpdateResult => {
  const queryClient = useQueryClient();
  const { plan } = useCurrentUser();
  const [isWaiting, setIsWaiting] = useState(enabled);
  const [didTimeout, setDidTimeout] = useState(false);
  const [hasPollingResolved, setHasPollingResolved] = useState(false);
  const startTimeRef = useRef<number>(Date.now());

  const checkAndPoll = useCallback(() => {
    const elapsed = Date.now() - startTimeRef.current;
    const keepPolling = shouldKeepPolling(plan, enabled, elapsed, timeoutMs);

    if (!keepPolling) {
      setIsWaiting(false);
      setHasPollingResolved(true);
      if (elapsed >= timeoutMs && plan === Plan_Type_Enum.Free) {
        setDidTimeout(true);
      }
      return;
    }

    queryClient.invalidateQueries({ queryKey: [TanstackQueryName.GetCurrentUser] });
  }, [plan, enabled, timeoutMs, queryClient]);

  useEffect(() => {
    if (!enabled) {
      setIsWaiting(false);
      setHasPollingResolved(false);
      return;
    }

    startTimeRef.current = Date.now();
    setIsWaiting(true);
    setHasPollingResolved(false);

    // Check immediately in case plan is already updated
    checkAndPoll();
  }, [enabled]);

  useEffect(() => {
    if (!isWaiting || !enabled) return;

    const intervalId = setInterval(() => {
      checkAndPoll();
    }, intervalMs);

    return () => clearInterval(intervalId);
  }, [isWaiting, enabled, intervalMs, checkAndPoll]);

  // React to plan changes from query invalidation
  useEffect(() => {
    if (!enabled || !isWaiting) return;

    if (plan && plan !== Plan_Type_Enum.Free) {
      setIsWaiting(false);
      setHasPollingResolved(true);
    }
  }, [plan, enabled, isWaiting]);

  return { isWaiting, didTimeout, hasPollingResolved };
};
